import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import type { SchedulesRibbon as SchedulesRibbonData } from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import { cn } from '~/utils/cn';

/**
 * Metric ribbon above the schedules list. Every number here comes from
 * `GET /schedules/ribbon`, computed server-side over the whole filtered set —
 * deliberately not summed from the loaded rows, which would only describe page
 * one and would visibly change as the user pages.
 */
export function SchedulesRibbon({
  data,
  isLoading,
}: {
  data: SchedulesRibbonData | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation('catalog-operations');

  if (isLoading || !data) {
    return (
      <Skeleton data-testid="schedules-ribbon-loading" className="h-[74px] w-full rounded-xl" />
    );
  }

  const cells: MetricRibbonCell[] = [
    { id: 'total', label: t('schedules.ribbon.total'), value: data.total.toLocaleString() },
    { id: 'active', label: t('schedules.ribbon.active'), value: data.active.toLocaleString() },
    { id: 'paused', label: t('schedules.ribbon.paused'), value: data.paused.toLocaleString() },
    { id: 'runs', label: t('schedules.ribbon.runs'), value: data.runCount.toLocaleString() },
    {
      id: 'success-rate',
      label: t('schedules.ribbon.successRate'),
      // null = no runs in the window. An em dash, never 0% — those mean
      // opposite things to whoever reads the row.
      value: data.successRate === null ? '—' : `${String(Math.round(data.successRate * 100))}%`,
      dim: data.successRate === null,
    },
    {
      id: 'products',
      label: t('schedules.ribbon.products'),
      value: data.productsEnriched.toLocaleString(),
    },
  ];

  return (
    <MetricRibbon
      cells={cells}
      testId="schedules-ribbon"
      className="sm:grid-cols-3 lg:grid-cols-6"
    />
  );
}

/** One ribbon cell. `dim` greys a placeholder value (e.g. "—" for no runs); `tone` colours a
 *  value whose meaning is the colour (a failure count, a success rate). */
export interface MetricRibbonCell {
  id: string;
  label: string;
  value: string;
  dim?: boolean | undefined;
  tone?: 'success' | 'warning' | 'error' | undefined;
  /** Muted secondary line under the value. */
  hint?: string | undefined;
}

const TONE_CLASS: Record<NonNullable<MetricRibbonCell['tone']>, string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

/**
 * The bordered strip of labelled numbers used above schedule lists — here and on Platform ▸
 * Schedules. Columns are set by the caller (`className`, base 2). Dividers are the 1px grid gap
 * over a border-coloured backdrop, so they stay right at every column count; pick counts that
 * fill whole rows.
 */
export function MetricRibbon({
  cells,
  testId,
  className,
  cellClassName,
}: {
  cells: MetricRibbonCell[];
  /** Root id; cells derive `${testId}-${id}-card` / `-value`. */
  testId: string;
  className?: string;
  cellClassName?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'bg-border grid grid-cols-2 gap-px overflow-hidden rounded-xl border',
        className,
      )}
    >
      {cells.map((cell) => (
        <div
          key={cell.id}
          data-testid={`${testId}-${cell.id}-card`}
          className={cn('bg-background min-w-0 px-4 py-3', cellClassName)}
        >
          <p className="text-muted-foreground truncate text-xs font-semibold tracking-wide uppercase">
            {cell.label}
          </p>
          <p
            data-testid={`${testId}-${cell.id}-value`}
            className={cn(
              'mt-0.5 truncate text-xl font-semibold tabular-nums',
              cell.tone && TONE_CLASS[cell.tone],
              cell.dim && 'text-muted-foreground font-medium',
            )}
          >
            {cell.value}
          </p>
          {cell.hint && (
            <p className="text-muted-foreground mt-0.5 truncate text-xs tabular-nums">
              {cell.hint}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
